import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO } from 'date-fns';
import { useIsMounted } from '@topconagriculture/frontend-utils';
export const I18nFormatContext = createContext({
    formatDate: () => '',
    formatDateTime: () => '',
    formatNumber: () => '',
    formatPercentage: () => '',
    formatPrice: () => '',
    locale: undefined,
});
function dateFormat(date, formatToken, options) {
    if (date === undefined || date === null) {
        return '';
    }
    if (!isValid(new Date(date))) {
        return '';
    }
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    return format(parsedDate, formatToken, options);
}
function numberFormat(value, locale, options) {
    if (value === undefined || value === null) {
        return '';
    }
    return new Intl.NumberFormat(locale, options).format(value);
}
export const I18nFormatProvider = ({ children, defaultDateFormat, defaultDateTimeFormat, }) => {
    const [locale, setLocale] = useState();
    const { i18n } = useTranslation();
    const isMounted = useIsMounted();
    useEffect(() => {
        const localeCode = i18n.language === 'en' ? 'en-US' : i18n.language;
        async function loadLocale(code) {
            if (!isMounted.current) {
                return;
            }
            const data = await import(`date-fns/locale/${code}/index.js`);
            isMounted.current && setLocale(data);
        }
        loadLocale(localeCode);
    }, [i18n.language]);
    const formatDate = useCallback((date, overrideFormat) => {
        return dateFormat(date, overrideFormat || defaultDateFormat, { locale });
    }, [defaultDateFormat, locale === null || locale === void 0 ? void 0 : locale.code]);
    const formatDateTime = useCallback((date, overrideFormat) => {
        return dateFormat(date, overrideFormat || defaultDateTimeFormat, { locale });
    }, [defaultDateTimeFormat, locale === null || locale === void 0 ? void 0 : locale.code]);
    const formatNumber = useCallback((value, options) => {
        return numberFormat(value, i18n.language, { ...options, style: 'decimal' });
    }, [i18n.language]);
    const formatPrice = useCallback((value, currency, options) => {
        return numberFormat(value, i18n.language, { ...options, style: 'currency', currency });
    }, [i18n.language]);
    const formatPercentage = useCallback((value, options) => {
        return numberFormat(value, i18n.language, { ...options, style: 'percent' });
    }, [i18n.language]);
    return (React.createElement(I18nFormatContext.Provider, { value: { formatDate, formatDateTime, formatNumber, formatPrice, formatPercentage, locale } }, children));
};
export const useI18nFormat = () => useContext(I18nFormatContext);
//# sourceMappingURL=I18nFormatContext.js.map